import { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetUnitOfMeasureTypes } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IUnitOfMeasureType, UnitOfMeasureType } from '../../../@types/model/masterData/unitOfMeasureType/unitOfMeasureType';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import UnitOfMeasureTypeHttpService from '../../../services/http/masterData/unitOfMeasureTypeHttpService';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IUnitOfMeasureTypeFormValues, UnitOfMeasureTypeFormValues } from '../../../@types/model/masterData/unitOfMeasureType/unitOfMeasureTypeFormValues';
import UnitOfMeasureTypeForm from './form/UnitOfMeasureTypeForm';
import { IApiError } from '../../../@types/other';
import { CustomTable, ConfirmationDialog } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { setUnitOfMeasureTypeMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useHistory } from 'react-router';

const UnitOfMeasureTypeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const unitOfMeasureTypes = useAppSelector(x => x.masterData.unitOfMeasureTypes);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedUnitOfMeasureType, setSelectedUnitOfMeasureType] = useState<IUnitOfMeasureType>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingUnitOfMeasureType, setDeletingUnitOfMeasureType] = useState<IUnitOfMeasureType>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await UnitOfMeasureTypeHttpService.getUnitOfMeasureTypeData();
                    dispatch(dataSetUnitOfMeasureTypes(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading unit of measure types.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedUnitOfMeasureType(undefined);
        setFormGuid(undefined);
    };

    const editUnitOfMeasureType = (unitOfMeasureType : IUnitOfMeasureType) => {
        setSelectedUnitOfMeasureType(unitOfMeasureType);
        setFormGuid(unitOfMeasureType.guid);
        openDialog();
    };

    const onSubmit = async (values : IUnitOfMeasureTypeFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await UnitOfMeasureTypeHttpService.addOrUpdateUnitOfMeasureType(UnitOfMeasureType(payload));
            const updatedList = upsertArrayElement(unitOfMeasureTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetUnitOfMeasureTypes(updatedList));
            await setUnitOfMeasureTypeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedUnitOfMeasureType ? 'Unit Of Measure Type updated successfully.' : 'Unit Of Measure Type added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating unit of measure type data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (unitOfMeasureType : IUnitOfMeasureType) => {
        setDeletingUnitOfMeasureType(unitOfMeasureType);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingUnitOfMeasureType(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeUnitOfMeasureType = async () => {
        if (!deletingUnitOfMeasureType) return;
        const updatedUnitOfMeasureType = { ...deletingUnitOfMeasureType, isActive: false };
        setIsLoading(true);
        try {
            const res = await UnitOfMeasureTypeHttpService.deleteUnitOfMeasureType(updatedUnitOfMeasureType.id);
            const updatedList = upsertArrayElement(unitOfMeasureTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetUnitOfMeasureTypes(updatedList));
            await setUnitOfMeasureTypeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Unit Of Measure Type deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting unit of measure type.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return UnitOfMeasureTypeFormValues(selectedUnitOfMeasureType);
        }
    }, [selectedUnitOfMeasureType, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Unit Of Measure Type'
                    isEdit={!!selectedUnitOfMeasureType}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <UnitOfMeasureTypeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IUnitOfMeasureType>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editUnitOfMeasureType}
                        enableEditing={hasEditingRight}
                        enableDeleting={umt => umt.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={unitOfMeasureTypes}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Unit Of Measure Type'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this unit of measure type?'
                onAccept={removeUnitOfMeasureType}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default UnitOfMeasureTypeScreen;
